import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';
import { Popover } from 'antd';
import { ExternalLink } from 'lucide-react';
import { IpcGradeTag } from '../components/ui';
import type { OpportunityLinkedIpdVO, OpportunityLinkedIpcVO } from '../api/ipc/opportunity';
import { openProjectScheduleDetail } from '../utils/projectScheduleNav';

export type PlanLaneNode = {
  planProjectId: number;
  workItemId?: number;
  name: string;
  productLine: string;
  category?: string;
  grade: string;
  stage: string;
  delayMonths: number;
  commitDate?: string | null;
  scheduleTargetDate?: string | null;
  actualInboundDate?: string | null;
  suggestedBriefDate?: string | null;
  moldType?: string;
  needCert?: string;
  axisMonth?: string;
  isNew?: boolean;
  linkedIpds?: OpportunityLinkedIpdVO[];
  linkedIpcs?: OpportunityLinkedIpcVO[];
};

function dash(v?: string | null): string {
  const t = (v || '').trim();
  return t || '—';
}

function slashPair(a?: string | null, b?: string | null): string {
  return `${dash(a)} / ${dash(b)}`;
}

function ipdStoryDetailUrl(projectKey: string, workItemId: number): string {
  return `https://project.feishu.cn/${encodeURIComponent((projectKey || '').trim())}/story/detail/${workItemId}`;
}

function linkLabel(code?: string, name?: string, fallback?: string): string {
  const c = (code || '').trim();
  const n = (name || '').trim();
  if (c && n) return `${c} ${n}`;
  return c || n || fallback || '—';
}

type Props = {
  nodes: PlanLaneNode[];
  /** 轴起止 YYYY-MM；不传则取数据 min/max */
  monthFrom?: string;
  monthTo?: string;
  /** 时间轴缩放倍率，1 = 铺满当前宽度；默认 1 */
  zoom?: number;
  /** Ctrl/⌘ + 滚轮缩放回调（倍率） */
  onZoomChange?: (zoom: number) => void;
};

/** 月列最小宽度（缩放=1 且需铺满时的下限）；缩放后可更窄 */
const MON_W_MIN = 56;
const MON_W_ZOOM_FLOOR = 20;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const HEAD_W = 88;
const ROW_H_MIN = 72;
const TOP0 = 34;
const LABEL_LINE_H = 18;
const LABEL_MAX_W = 200;
const LABEL_PAD_X = 8; // 与 .pv-lbl padding 左右之和一致（border-box）
const LABEL_H_FUDGE = 4; // 估算高度余量，避免实测换行后压轴
const STEM_BASE = 14; // 圆点/轴线到文案近边的最小间距（避免压住轴线）
const COLLIDE_PAD = 8;
const PLACE_STEP = 4; // 纵向避让步进（像素）

function parseYm(ym?: string | null): number | null {
  const m = String(ym || '').match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 12 + parseInt(m[2], 10) - 1;
}

function quarterLabel(idx: number): string {
  const y = Math.floor(idx / 12);
  const m = (idx % 12) + 1;
  const q = Math.floor((m - 1) / 3) + 1;
  return `${y} Q${q}`;
}

function nodeAxis(n: PlanLaneNode): string {
  return (n.axisMonth || (n.commitDate ? String(n.commitDate).slice(0, 7) : '')).slice(0, 7);
}

function cleanName(name: string): string {
  return String(name || '')
    .replace(/（.*?）|\(.*?\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stageKey(stage: string): 'inProgress' | 'done' | 'term' | 'notstart' {
  if (stage === '已完成') return 'done';
  if (stage === '已终止') return 'term';
  if (stage === '尚未进行' || stage === '待立项') return 'notstart';
  return 'inProgress';
}

function stageIcon(key: ReturnType<typeof stageKey>): string {
  if (key === 'done') return '✓';
  if (key === 'term') return '✕';
  return '';
}

function stageColor(key: ReturnType<typeof stageKey>): string {
  if (key === 'done') return '#15803D';
  if (key === 'term') return '#B42318';
  if (key === 'notstart') return '#94A3B8';
  return '#3B82F6';
}

function fmtDate(v?: string | null): string {
  if (!v) return '—';
  return String(v).slice(0, 10);
}

/**
 * 估算标签宽高：默认单行；超出 LABEL_MAX_W 时换行。
 * 宽度含 padding（与 border-box 一致），并留字宽余量，避免末字被挤换行后高度低估压住轴线。
 */
function estimateLabelBox(name: string, isNew: boolean): { w: number; h: number; lines: number } {
  const badgeW = isNew ? 32 : 0;
  // 略大于实测，宁可多留宽也不要意外换行
  const charW = (ch: string) => (/[\u4e00-\u9fff]/.test(ch) ? 14.5 : 9);
  let textW = 0;
  for (const ch of name) textW += charW(ch);
  const contentW = textW + badgeW;
  const naturalOuter = contentW + LABEL_PAD_X;
  if (naturalOuter <= LABEL_MAX_W) {
    return {
      w: Math.max(48, Math.ceil(naturalOuter + 2)),
      h: LABEL_LINE_H,
      lines: 1,
    };
  }

  // border-box：可用内容宽 = 外宽 - padding
  const maxContent = LABEL_MAX_W - LABEL_PAD_X;
  let lines = 1;
  let lineW = badgeW;
  let maxLineW = 0;
  for (const ch of name) {
    const cw = charW(ch);
    if (lineW + cw > maxContent && lineW > 0) {
      maxLineW = Math.max(maxLineW, lineW);
      lines += 1;
      lineW = cw;
    } else {
      lineW += cw;
    }
  }
  maxLineW = Math.max(maxLineW, lineW);
  return {
    w: LABEL_MAX_W,
    h: Math.max(LABEL_LINE_H, lines * LABEL_LINE_H),
    lines,
  };
}

type RawMark = {
  id: string;
  name: string;
  stage: string;
  grade: string;
  productLine: string;
  category: string;
  delayMonths: number;
  isNew: boolean;
  axis: string;
  commitDate?: string | null;
  scheduleTargetDate?: string | null;
  suggestedBriefDate?: string | null;
  moldType: string;
  needCert: string;
  linkedIpds: OpportunityLinkedIpdVO[];
  linkedIpcs: OpportunityLinkedIpcVO[];
  cx: number;
  sk: ReturnType<typeof stageKey>;
  icon: string;
  labelW: number;
  labelH: number;
  labelLines: number;
};

type PlacedMark = RawMark & {
  lineY: number;
  side: 1 | -1;
  stemLen: number;
  labelTop: number;
};

/**
 * 同泳道内按 x 贪心分配上下错层，避免文案二维碰撞。
 * side=1 在轴上方，side=-1 在轴下方；多行标签按实际高度避让。
 * 标签近边距轴至少 STEM_BASE，避免压住轴线。
 * 返回的 maxAbove/maxBelow 为轴到最远标签外缘的像素距离。
 */
function placeLaneLabels(raw: RawMark[]): {
  marks: Omit<PlacedMark, 'lineY'>[];
  maxAbove: number;
  maxBelow: number;
} {
  const sorted = [...raw].sort((a, b) => a.cx - b.cx || a.name.localeCompare(b.name, 'zh'));
  const occupied: { side: 1 | -1; top: number; bottom: number; left: number; right: number }[] = [];
  const marks: Omit<PlacedMark, 'lineY'>[] = [];
  let maxAbove = 0;
  let maxBelow = 0;
  const maxDist = STEM_BASE + 16 * LABEL_LINE_H;

  for (const m of sorted) {
    const placeH = m.labelH + LABEL_H_FUDGE;
    const half = m.labelW / 2;
    const left = m.cx - half - COLLIDE_PAD;
    const right = m.cx + half + COLLIDE_PAD;
    let side: 1 | -1 = 1;
    let stemLen = STEM_BASE;
    let labelTop = -(STEM_BASE + placeH);
    let found = false;

    for (let dist = STEM_BASE; dist <= maxDist && !found; dist += PLACE_STEP) {
      for (const sd of [1, -1] as const) {
        // 近边贴在 ±dist，远边再扩 placeH；保证 [|近边|, ∞) 与轴线间隙 >= STEM_BASE
        const top = sd === 1 ? -(dist + placeH) : dist;
        const bottom = sd === 1 ? -dist : dist + placeH;
        const hit = occupied.some(
          o => o.side === sd && left < o.right && right > o.left && top < o.bottom && bottom > o.top,
        );
        if (!hit) {
          side = sd;
          stemLen = dist;
          // 渲染用真实 labelH（不含 fudge），近边仍落在 ±dist
          labelTop = sd === 1 ? -(dist + m.labelH) : dist;
          found = true;
          occupied.push({ side: sd, top, bottom, left, right });
          break;
        }
      }
    }

    if (!found) {
      side = marks.length % 2 === 0 ? 1 : -1;
      stemLen = STEM_BASE + Math.floor(marks.length / 2) * LABEL_LINE_H;
      labelTop = side === 1 ? -(stemLen + m.labelH) : stemLen;
      const top = side === 1 ? -(stemLen + placeH) : stemLen;
      const bottom = side === 1 ? -stemLen : stemLen + placeH;
      occupied.push({ side, top, bottom, left, right });
    }

    if (side === 1) maxAbove = Math.max(maxAbove, stemLen + placeH);
    else maxBelow = Math.max(maxBelow, stemLen + placeH);

    marks.push({
      ...m,
      side,
      stemLen,
      labelTop,
    });
  }

  return { marks, maxAbove, maxBelow };
}

function buildMarkTip(m: PlacedMark): ReactNode {
  return (
    <div className="pv-tip" onClick={e => e.stopPropagation()}>
      <div className="pv-tip__name">{m.name}</div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">执行阶段</span>
        <span className="pv-tip__v">
          <i className="pv-tip__dot" style={{ background: stageColor(m.sk) }} />
          {m.stage || '—'}
        </span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">品线 / 品类</span>
        <span className="pv-tip__v">{slashPair(m.productLine, m.category)}</span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">定级</span>
        <span className="pv-tip__v"><IpcGradeTag grade={m.grade} /></span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">目标入库时间</span>
        <span className="pv-tip__v">
          {fmtDate(m.commitDate) !== '—' ? fmtDate(m.commitDate) : (m.axis || '—')}
        </span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">开发预估入库时间</span>
        <span className="pv-tip__v">
          {fmtDate(m.scheduleTargetDate)}
        </span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">建议 Brief</span>
        <span className="pv-tip__v">{fmtDate(m.suggestedBriefDate)}</span>
      </div>
      <div className="pv-tip__row">
        <span className="pv-tip__k">开模 / 特证</span>
        <span className="pv-tip__v">{slashPair(m.moldType, m.needCert)}</span>
      </div>
      {m.stage !== '待立项' && (m.linkedIpds.length > 0 || m.linkedIpcs.length > 0) && (
        <>
          <div className="pv-tip__links">
            <div className="pv-tip__links-title">关联 IPD</div>
            {m.linkedIpds.length === 0 ? (
              <div className="pv-tip__links-empty">暂无</div>
            ) : (
              m.linkedIpds.map(item => {
                const canOpen = !!(item.projectKey || '').trim() && item.workItemId > 0;
                const label = linkLabel(item.projectCode, item.name, `IPD #${item.workItemId}`);
                return (
                  <div key={`ipd-${item.projectKey}-${item.workItemId}`} className="pv-tip__link-row">
                    <span title={label}>{label}</span>
                    {canOpen && (
                      <button
                        type="button"
                        className="pv-tip__link-btn"
                        title="打开飞书 IPD"
                        onClick={e => {
                          e.stopPropagation();
                          window.open(ipdStoryDetailUrl(item.projectKey, item.workItemId), '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <ExternalLink size={12} aria-hidden />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          <div className="pv-tip__links">
            <div className="pv-tip__links-title">关联 IPC</div>
            {m.linkedIpcs.length === 0 ? (
              <div className="pv-tip__links-empty">暂无</div>
            ) : (
              m.linkedIpcs.map(item => {
                const label = linkLabel(item.code, item.name, `IPC #${item.projectId}`);
                return (
                  <div key={`ipc-${item.projectId}`} className="pv-tip__link-row">
                    <span title={label}>{label}</span>
                    <button
                      type="button"
                      className="pv-tip__link-btn"
                      title="打开 IPC 项目详情"
                      onClick={e => {
                        e.stopPropagation();
                        openProjectScheduleDetail(item.projectId, item.currentScheduleId);
                      }}
                    >
                      <ExternalLink size={12} aria-hidden />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

export const PlanLaunchLane: FC<Props> = ({ nodes, monthFrom, monthTo, zoom = 1, onZoomChange }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [availW, setAvailW] = useState(0);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setAvailW(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !onZoomChange) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      // 向下滚缩小，向上滚放大；步进约 10%
      const step = e.deltaY > 0 ? -0.1 : 0.1;
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((zoomRef.current + step) * 10) / 10));
      if (next !== zoomRef.current) onZoomChange(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onZoomChange]);

  const range = useMemo(() => {
    if (!nodes.length) return null;
    const indices = nodes
      .map(n => parseYm(nodeAxis(n)))
      .filter((x): x is number => x != null);
    if (!indices.length) return null;
    const dataMin = Math.min(...indices);
    const dataMax = Math.max(...indices);
    const start = parseYm(monthFrom) ?? dataMin;
    const end = parseYm(monthTo) ?? dataMax;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    return {
      lo,
      hi,
      monthCount: hi - lo + 1,
      lines: Array.from(new Set(nodes.map(n => n.productLine || '未分类'))),
    };
  }, [nodes, monthFrom, monthTo]);

  const fitMonW = useMemo(() => {
    if (!range || range.monthCount <= 0) return MON_W_MIN;
    if (availW <= HEAD_W) return MON_W_MIN;
    const fit = Math.floor((availW - HEAD_W) / range.monthCount);
    return Math.max(MON_W_MIN, fit);
  }, [availW, range]);

  const monW = Math.max(MON_W_ZOOM_FLOOR, Math.round(fitMonW * (Number.isFinite(zoom) && zoom > 0 ? zoom : 1)));

  const model = useMemo(() => {
    if (!range) return null;
    const { lo, hi, monthCount, lines } = range;
    const canvasW = HEAD_W + monthCount * monW;

    const ticks: { left: number; major: boolean; label?: string }[] = [];
    for (let i = lo; i <= hi; i += 1) {
      const left = (i - lo) * monW;
      const monthOfYear = ((i % 12) + 12) % 12;
      if (monthOfYear % 3 === 0) {
        ticks.push({ left, major: true, label: quarterLabel(i) });
      } else {
        ticks.push({ left, major: false });
      }
    }

    const rawByLine: RawMark[][] = lines.map(() => []);
    nodes.forEach(n => {
      const axis = nodeAxis(n);
      const mi = parseYm(axis);
      if (mi == null || mi < lo || mi > hi) return;
      const line = n.productLine || '未分类';
      const li = lines.indexOf(line);
      if (li < 0) return;
      const name = cleanName(n.name) || n.name;
      const isNew = !!n.isNew;
      const sk = stageKey(n.stage);
      const box = estimateLabelBox(name, isNew);
      rawByLine[li].push({
        id: n.planProjectId > 0 ? `p-${n.planProjectId}` : `w-${n.workItemId || n.name}`,
        name,
        stage: n.stage,
        grade: n.grade,
        productLine: n.productLine || (n.isNew ? '规划外' : ''),
        category: n.category || '',
        delayMonths: n.delayMonths || 0,
        isNew,
        axis,
        commitDate: n.commitDate,
        scheduleTargetDate: n.scheduleTargetDate,
        suggestedBriefDate: n.suggestedBriefDate,
        moldType: n.moldType || '',
        needCert: n.needCert || '',
        linkedIpds: n.linkedIpds || [],
        linkedIpcs: n.linkedIpcs || [],
        cx: HEAD_W + (mi - lo) * monW + monW / 2,
        sk,
        icon: stageIcon(sk),
        labelW: box.w,
        labelH: box.h,
        labelLines: box.lines,
      });
    });

    const laneLineYs: number[] = [];
    let yCursor = TOP0;
    const marks: PlacedMark[] = [];

    lines.forEach((_, li) => {
      const { marks: placed, maxAbove, maxBelow } = placeLaneLabels(rawByLine[li]);
      const padTop = Math.max(18, maxAbove + 6);
      const padBottom = Math.max(18, maxBelow + 6);
      const rowH = Math.max(ROW_H_MIN, padTop + padBottom);
      const lineY = yCursor + padTop;
      laneLineYs.push(lineY);
      placed.forEach(m => marks.push({ ...m, lineY }));
      yCursor += rowH;
    });

    return {
      lines,
      canvasW,
      canvasH: yCursor + 10,
      ticks,
      monthCount,
      marks,
      laneLineYs,
      lineCounts: lines.map(ln => nodes.filter(n => (n.productLine || '未分类') === ln).length),
    };
  }, [nodes, range, monW]);

  if (!nodes.length) {
    return <div className="plan-board-empty">当前筛选条件下暂无项目</div>;
  }

  if (!model) {
    return <div className="plan-board-empty">暂无时间轴数据</div>;
  }

  const {
    lines, canvasW, canvasH, ticks, marks, lineCounts, monthCount, laneLineYs,
  } = model;

  return (
    <div className="plan-lane-scroll" id="laneBox" ref={wrapRef}>
      <div className="pv-lane-canvas" style={{ width: canvasW, minWidth: '100%', height: canvasH }}>
        <div className="pv-lane-head" style={{ top: 0, height: 32, width: HEAD_W }} />
        <div className="pv-axis" style={{ left: HEAD_W, top: 0, width: monthCount * monW }}>
          {ticks.map((t, i) => (
            <div key={i}>
              <div
                className={t.major ? 'pv-tk-major' : 'pv-tk-minor'}
                style={{ left: t.left }}
              />
              {t.label && (
                <div className="pv-tk-label" style={{ left: t.left + 3 }}>{t.label}</div>
              )}
            </div>
          ))}
        </div>

        {lines.map((ln, li) => (
          <div key={ln}>
            <div className="pv-lane-head" style={{ top: laneLineYs[li] - 10, width: HEAD_W }}>
              <b>{ln}</b>
              <span>{lineCounts[li]}</span>
            </div>
            <div
              className="pv-lane-line"
              style={{ left: HEAD_W, top: laneLineYs[li], width: monthCount * monW }}
            />
          </div>
        ))}

        {marks.map(m => (
          <Popover
            key={m.id}
            placement="top"
            trigger="hover"
            mouseEnterDelay={0.08}
            mouseLeaveDelay={0.12}
            rootClassName="pv-lane-popover"
            styles={{
              body: {
                width: 280,
                maxWidth: 280,
                padding: '8px 10px',
                boxSizing: 'border-box',
              },
            }}
            content={<div className="pv-tip-wrap">{buildMarkTip(m)}</div>}
          >
            <span className="pv-mark-hit" style={{ left: m.cx, top: m.lineY }}>
              <span className="pv-mark">
                <i
                  className="pv-stem"
                  style={
                    m.side === 1
                      ? { top: -m.stemLen, height: m.stemLen }
                      : { top: 0, height: m.stemLen }
                  }
                  aria-hidden
                />
                <span
                  className={[
                    'pv-dot',
                    `pv-dot--${m.sk}`,
                    m.delayMonths > 0 ? 'pv-dot--delay' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {m.icon}
                </span>
                <span
                  className={[
                    'pv-lbl',
                    `pv-lbl--${m.sk}`,
                    m.isNew ? 'pv-lbl--new' : '',
                  ].filter(Boolean).join(' ')}
                  style={
                    m.labelLines > 1
                      ? { top: m.labelTop, width: m.labelW, maxWidth: m.labelW }
                      : { top: m.labelTop, maxWidth: m.labelW, whiteSpace: 'nowrap' as const }
                  }
                >
                  {m.name}
                  {m.isNew && <span className="pv-new-badge">new</span>}
                </span>
              </span>
            </span>
          </Popover>
        ))}
      </div>
    </div>
  );
};

export default PlanLaunchLane;
